import { useMemo } from 'react'
import { useAppSelector } from '@/redux/hooks'

interface ColorVariant {
  main: string
  light: string
  dark: string
  contrastText: string
}

export interface Theme {
  colors: {
    common: { white: string; black: string }
    primary: ColorVariant
    secondary: ColorVariant
    success: ColorVariant
    error: ColorVariant
    grey: [string, string, string, string, string, string]
  }
  borderRadius: string
}

// Static defaults ported from the design-system DefaultTheme. The per-ecommerce
// brand values (primary/secondary/borderRadius) are overlaid from
// settings.branding; everything else is constant across ecommerces.
const DEFAULT_THEME: Theme = {
  colors: {
    common: { white: '#ffffff', black: '#000000' },
    primary: {
      main: '#1b75eb',
      light: '#63a2f4',
      dark: '#0148a5',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#10fab8',
      light: '#a6fce4',
      dark: '#02dd9f',
      contrastText: '#000'
    },
    success: {
      main: '#00b0a6',
      light: '#e6f7f6',
      dark: '#00716a',
      contrastText: '#ffffff'
    },
    error: {
      main: '#ff5962',
      light: '#feeeef',
      dark: '#c3373f',
      contrastText: '#ffffff'
    },
    grey: ['#808080', '#999999', '#B3B3B3', '#CCCCCC', '#E5E5E5', '#F2F2F2']
  },
  borderRadius: '73px'
}

/**
 * Brand theme sourced directly from the ecommerce settings in redux, replacing
 * the design-system ThemeProvider/useTheme. Returns the same shape the call
 * sites already consume.
 */
export const useTheme = (): Theme => {
  const branding = useAppSelector(
    (store) => store.settingsApi.settings.response.branding
  )

  return useMemo(() => {
    if (!branding) return DEFAULT_THEME

    // Mirror the design-system's intersectThemes: a brand color only overrides
    // the default when ALL of its variants are set. A partial config (e.g. only
    // `main`) is discarded so the default palette wins — matching production.
    const hasFullPrimary =
      !!branding.primary_theme_color_main &&
      !!branding.primary_theme_color_light &&
      !!branding.primary_theme_color_dark &&
      !!branding.primary_theme_color_contrast_text

    const hasFullSecondary =
      !!branding.secondary_theme_color_main &&
      !!branding.secondary_theme_color_light &&
      !!branding.secondary_theme_color_dark &&
      !!branding.secondary_theme_color_contrast_text

    return {
      colors: {
        ...DEFAULT_THEME.colors,
        primary: hasFullPrimary
          ? {
              main: branding.primary_theme_color_main as string,
              light: branding.primary_theme_color_light as string,
              dark: branding.primary_theme_color_dark as string,
              contrastText: branding.primary_theme_color_contrast_text as string
            }
          : DEFAULT_THEME.colors.primary,
        secondary: hasFullSecondary
          ? {
              main: branding.secondary_theme_color_main as string,
              light: branding.secondary_theme_color_light as string,
              dark: branding.secondary_theme_color_dark as string,
              contrastText:
                branding.secondary_theme_color_contrast_text as string
            }
          : DEFAULT_THEME.colors.secondary
      },
      borderRadius: branding.border_radius || DEFAULT_THEME.borderRadius
    }
  }, [branding])
}
